import { Inject, Injectable } from '@angular/core';
import { ScreenDimensions } from '../shared/models/screen.dimensions.model';
import { Observable, of, forkJoin } from 'rxjs';
import { SerializedParams } from './models/serialized-param.model';
import { BookmarkModel } from '../bookmarks/models/bookmark.model';
import { CameraService } from '../engine/camera.service';
import { NumberService } from '../shared/number.service';
import { CameraModel } from '../engine/models/camera.model';
import { WidgetOptions } from './models/widget-options.model';
import { map, catchError, switchMap } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { UserAppModel } from '../auth/models/user-app.model';
import { PublishExportModel } from './models/publish-export.model';
import { DownloadEntryModel } from './models/download-entry.model';
import { defaultScreen } from './data/screens';
import { get, isNil } from 'lodash';
import { UrlUIConfig } from '../widget-config/initial-config';
import { environment } from 'src/environments/environment';
import { EnvironmentDeploy } from 'src/environments/models/environment-deploy';
import { exportCSV } from '../shared/libraries/export/csv';
import { ViewQuizModeEnum } from '../view/models/view-quiz-mode.enum';
import { formatDate } from '@angular/common';
import { CollectionModel } from '../organizations/models/collection.model';
import { OrganizationsService } from '../organizations/organizations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { APP_BASE_URL, VIEWER_BASE_URL } from '../core/config/app-config/app-config.module';

export enum PublishLinkType {
  Single = 'share.widget.link',
  Bulk = 'share.widget.export',
}

const formHeaders = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8',
  }),
};

const defaultOptions = new WidgetOptions();

const embedDefaults = {
  id: 'embedded-human',
  dimensions: defaultScreen.dimensions,
  fullScreen: true,
};

@Injectable({
  providedIn: 'root',
})
export class PublishService {
  constructor(
    private http: HttpClient,
    private cameraService: CameraService,
    private numberService: NumberService,
    private orgnanizationService: OrganizationsService,
    private matSnackbar: MatSnackBar,
    @Inject(APP_BASE_URL) private APP_BASE_URL: string,
    @Inject(VIEWER_BASE_URL) private VIEWER_BASE_URL: string,
  ) {}

  exportBookmarks(
    bookmarksToExport: BookmarkModel[],
    organizationId: number,
    collections: CollectionModel[],
    widgetPath: string,
    widgetOptions: WidgetOptions,
    dimensions?: ScreenDimensions,
    lang?: string,
    app?: UserAppModel,
    offlineEnabled: boolean = false,
    productAccountId: string = '',
  ): Observable<boolean> {
    // Some download can take a few seconds, so acknowledge the user's action
    this.matSnackbar.open(`Exporting ${bookmarksToExport.length} models to CSV...`);

    // Define the CSV export columns
    const userExportColumns = [
      { label: 'Title', field: 'title' },
      { label: 'ID', field: 'id' },
      { label: 'Collection', field: 'collection' },
      { label: 'URL', field: 'url' },
      { label: 'iFrame', field: 'iFrame' },
      { label: 'Author', field: 'author' },
      { label: 'Thumbnail URL', field: 'thumbnail_url' },
      { label: 'Created Date', field: 'create_date_time' },
      { label: 'Updated Date', field: 'update_date_time' },
    ];

    // Additional export columns for the offline build
    if (offlineEnabled) {
      userExportColumns.push({
        label: 'Offline Package URL',
        field: 'offline_url',
      });
    }

    // Generate a bulk export UAID (shared)
    const uaid$ = app ? of(null) : this.getUAID(PublishLinkType.Bulk);

    // General optional download links for the offline build
    const offlineURLs$ = offlineEnabled ? this.getDownloadURLs(bookmarksToExport) : of([]);

    // Bookmark data does not include which collection a bookmark is in. So we generate
    // a dictionary of bookmarks to collection by loading all the collections once.
    const collectionDictionary = new Map<string, CollectionModel>();
    const collections$ = collections.length
      ? forkJoin(
          collections.map((collection) => {
            return this.orgnanizationService
              .getCollectionContent(organizationId, collection.collection_id)
              .pipe(
                map((bookmarks) => {
                  bookmarks.forEach((bookmark) => {
                    collectionDictionary.set(bookmark.id, collection);
                  });
                }),
              );
          }),
        )
      : of(true);

    // Start the export
    return forkJoin([uaid$, offlineURLs$, collections$]).pipe(
      map(([uaid, offlineURLs]): PublishExportModel[] => {
        return bookmarksToExport.map((bookmark, index) => {
          // Prep variables
          const { id, title, author, thumbnail_url, create_date_time, update_date_time } = bookmark;

          // Lookup the collection name
          const collectionName = collectionDictionary.get(bookmark.id)?.collection_name ?? '';

          // Generate the diffent URL
          const url = this.getWidgetURL(
            bookmark,
            widgetPath,
            widgetOptions,
            null,
            uaid,
            lang,
            app,
            productAccountId,
          );

          const embed = this.getWidgetEmbed(url, dimensions);
          const thumbnailURL = this.buildThumbnailURL(thumbnail_url);
          const offlineURL = offlineURLs[index] || '';

          // Prep the dates
          const dateCreated = formatDate(new Date(create_date_time), 'shortDate', 'en');
          const dateUpdated = formatDate(new Date(update_date_time), 'shortDate', 'en');

          // Put the whole model together
          const exportModel: PublishExportModel = {
            title: title,
            id: id,
            collection: collectionName,
            url: url,
            iFrame: embed,
            author: author,
            thumbnail_url: thumbnailURL,
            create_date_time: dateCreated,
            update_date_time: dateUpdated,
            offline_url: offlineURL,
          };

          return exportModel;
        });
      }),

      // Export the models to csv
      switchMap((exportedModels: PublishExportModel[]): Observable<string> => {
        return exportCSV(exportedModels, userExportColumns);
      }),

      // Download the csv
      switchMap((csv) => {
        // get formatted export name (YYYY-MM-DD)
        const isoDate = new Date().toISOString().split('T')[0];
        const exportFileName = `BioDigital-Widgets-${isoDate}-export.csv`;
        const exportBlob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(exportBlob, exportFileName);

        return of(true);
      }),
    );
  }

  /**
   * Gets the widget URL
   */
  generateWidgetURL(
    model: BookmarkModel,
    widgetPath: string,
    widgetOptions: WidgetOptions,
    camera?: CameraModel,
    linkType?: PublishLinkType,
    lang?: string,
    app?: UserAppModel,
    productAccountId?: string,
  ): Observable<string> {
    // generate optional UAID
    const uaid$ = app || !linkType ? of(null) : this.getUAID(linkType);

    return uaid$.pipe(
      map((uaid) =>
        this.getWidgetURL(
          model,
          widgetPath,
          widgetOptions,
          camera,
          uaid,
          lang,
          app,
          productAccountId,
        ),
      ),
    );
  }

  getWidgetURL(
    model: BookmarkModel,
    widgetPath: string,
    widgetOptions: WidgetOptions,
    camera?: CameraModel,
    uaid?: string,
    lang: string = 'en',
    app?: UserAppModel,
    productAccountId?: string,
  ): string {
    widgetOptions = widgetOptions || defaultOptions;

    // add content params
    const isBookmark = model.type === 'bookmark';
    const contentParams = {};
    const contentType = isBookmark ? 'be' : 'm';
    const contentId = isBookmark ? model.id : model.module_name;
    contentParams[contentType] = `${contentId}`;

    // expand full list of params
    const serializedParams = {
      ...contentParams,
      ...this.getParamsForUIOptions(widgetOptions || defaultOptions),
      ...this.getParamsForLoadingExperience(widgetOptions || defaultOptions),
      ...this.getParamsForScrollMode(widgetOptions || defaultOptions),
      ...this.getParamsForLoadRotate(widgetOptions || defaultOptions),
      ...this.getParamsForLabelScale(widgetOptions || defaultOptions),
    };

    // smooth BG color
    const colors = get(widgetOptions, 'background.colors');
    if (Array.isArray(colors) && colors.length >= 3) {
      const smoothColor = colors
        .slice(0, 4) // rgb(a)
        .map((value) => this.numberService.roundNumber(value, 3));

      serializedParams['background.colors'] = `${smoothColor}`;
    }

    // smooth camera
    if (camera) {
      const { eye, look, up } = this.cameraService.roundCamera(camera, 3);
      const smoothCamera = [eye.x, eye.y, eye.z, look.x, look.y, look.z, up.x, up.y, up.z];
      serializedParams['camera'] = `${smoothCamera}`;
    }

    // append dev key or uaid
    if (app) {
      // append developer key
      serializedParams['dk'] = app.app_apikey;
    } else if (uaid) {
      // append UAID
      serializedParams['uaid'] = uaid;
    }

    // append lang
    if (lang && lang !== 'en') {
      serializedParams['lang'] = lang;
    }

    // append product account key (for reading widget settings)
    if (productAccountId) {
      serializedParams['paid'] = productAccountId;
    }

    // special case: always use custom widget
    const targetWidgetPath = this.isCustomWidget(model.embed_url_path)
      ? model.embed_url_path
      : widgetPath;
    return this.buildWidgetURL(targetWidgetPath, serializedParams);
  }

  /**
   * Gets the widget embed code
   */
  generateWidgetEmbed(
    model: BookmarkModel,
    widgetPath: string,
    widgetOptions: WidgetOptions,
    dimensions?: ScreenDimensions,
    camera?: CameraModel,
    linkType?: PublishLinkType,
    lang?: string,
    app?: UserAppModel,
    productAccountId?: string,
  ): Observable<string> {
    // get URL
    return this.generateWidgetURL(
      model,
      widgetPath,
      widgetOptions,
      camera,
      linkType,
      lang,
      app,
      productAccountId,
    ).pipe(
      // compose embed
      map((url) => this.getWidgetEmbed(url, dimensions)),
    );
  }

  getWidgetEmbed(url: string, dimensions?: ScreenDimensions): string {
    const { width, height } = dimensions || embedDefaults.dimensions;
    return `<iframe id="${embedDefaults.id}" frameBorder="0" width="${width}" height="${height}" allowFullScreen="${embedDefaults.fullScreen}" loading="lazy" src="${url}"></iframe>`;
  }

  getImageEmbed(title: string, widgetURL: string, imageURL: string): string {
    const imageTitle = `BioDigital Human: ${title}`.replace(/"/g, '&quot;'); // encode double quotes
    return `<a href="${widgetURL}" target="_blank"><img src="${imageURL}" alt="${imageTitle}" title="${imageTitle}" /></a>`;
  }

  getUAID(linkType: PublishLinkType): Observable<string> {
    const analytic = {
      analytic_type: `${linkType}`,
      analytic_platform: 'app',
    };
    return this.http
      .post<string>(`/ws/user/analytics`, analytic, formHeaders)
      .pipe(map((response: any) => response.id));
  }

  // generates an ordered list of download links for the specified bookmarks
  getDownloadURLs(models: BookmarkModel[]): Observable<string[]> {
    // create a list of download request models
    const contentModels: DownloadEntryModel[] = models
      .filter((bookmark) => !bookmark.favorite && !bookmark.unavailable) // exclude legacy content
      .map((bookmark) => {
        return {
          type: bookmark.type,
          id: bookmark.type === 'module' ? bookmark.module_name : bookmark.id,
        };
      });

    return this.http
      .post(
        `/ws/download/reserve`,
        { content: contentModels },
        { headers: { 'Content-Type': 'application/json' } },
      )
      .pipe(
        map((response: any) => {
          const tokens: DownloadEntryModel[] = response.tokens;
          // for each piece of content, generate download URL
          return models.map((bookmark) => {
            const type = bookmark.type;
            const id = bookmark.type === 'module' ? bookmark.module_name : bookmark.id;

            // find token by type, id
            const foundToken = tokens.find((token) => token.type === type && token.id === id);

            // build download from token
            return foundToken && foundToken.token ? this.buildDownloadURL(foundToken.token) : '';
          });
        }),
        catchError(() => of(models.map(() => ''))),
      );
  }

  /**
   * Overwrites the target widget options w/ source widget data.
   */
  extendWidgetOptions(targetWidgetOptions: WidgetOptions, sourceWidgetData: any): WidgetOptions {
    // expand flat options
    const sourceWidgetOptions = this.expandWidgetOptions(sourceWidgetData);

    // deep extend from source
    this.deepExtendWidgetOptions(targetWidgetOptions, sourceWidgetOptions);

    return targetWidgetOptions;
  }

  getWidgetConfig(widgetOptions: WidgetOptions): SerializedParams {
    const serializedParams: SerializedParams = {};
    const paramNames = Object.keys(widgetOptions);
    paramNames.forEach((paramName) => {
      const paramValue = widgetOptions[paramName];
      if (paramValue !== null) {
        const normalizedConfigName = this.getConfigName(paramName);
        serializedParams[normalizedConfigName] = paramValue;
      }
    });
    return serializedParams;
  }

  /**
   * i.e., uiInfo -> ui-info -> info
   */
  getConfigName(name: string): string {
    const dashCase = this.camelToDashCase(name);
    const normalizedDashCase = dashCase.replace(/^ui-/g, '');
    const configName = this.dashCaseToCamelCase(normalizedDashCase);
    return configName;
  }

  getParamsForLoadingExperience(params: WidgetOptions) {
    if (!params.autoLoad) {
      return { 'auto-load': false };
    }

    if (params.initial.button) {
      return { 'initial.button': true };
    }

    if (params.initial.handHint) {
      return { 'initial.hand': true };
    }

    if (params.initial.play) {
      return { 'initial.play': true };
    }

    return {};
  }

  getParamsForScrollMode(params: WidgetOptions) {
    if (params.disableScroll.alert) {
      return { 'disable-scroll.alert': true };
    }

    if (params.disableScroll.overlay) {
      return { 'disable-scroll': true };
    }

    return { 'disable-scroll': false };
  }

  getParamsForLoadRotate(params: WidgetOptions) {
    if (typeof params.loadRotate === 'number') {
      return { 'load-rotate': params.loadRotate };
    }

    return {};
  }

  getParamsForLabelScale(params: WidgetOptions) {
    // if (typeof params.labelScale === 'number') {
    //   return { 'label-scale': params.labelScale };
    // }

    return {};
  }

  getParamsForUIOptions(params: WidgetOptions): UrlUIConfig {
    return {
      'ui-info': params.uiInfo,
      'ui-search': params.uiSearch,
      'ui-undo': params.uiUndo,
      'ui-fullscreen': params.uiFullscreen,
      'ui-nav': params.uiNav,
      'ui-tools': params.uiTools,
      'ui-help': params.uiHelp,
      'ui-chapter-list': params.uiChapterList,
      'ui-label-list': params.uiLabelList,
      'ui-anatomy-descriptions': params.uiAnatomyDescriptions,
      'ui-tutorial': params.uiTutorial,
      'ui-loader': params.uiLoader,
      'ui-whiteboard': params.uiWhiteboard,
      'ui-layers': params.uiVisibilityLayers,
      'ui-anatomy-labels': params.uiAnatomyLabels,
      // quiz params
      ...this.getParamsForUIQuiz(params),
    };
  }

  getParamsForUIQuiz(params: WidgetOptions): UrlUIConfig {
    // build quiz params
    const uiQuizParams: UrlUIConfig = {};
    const uiQuizMode = get(params, 'uiQuiz.mode');
    const uiQuizNumber = get(params, 'uiQuiz.number');

    // disabled: optional
    if (uiQuizMode === ViewQuizModeEnum.Disabled) {
      uiQuizParams['ui-quiz'] = false;
    }

    // locked: optional
    if (uiQuizMode === ViewQuizModeEnum.Locked) {
      uiQuizParams['ui-quiz-locked'] = true;
    }

    // single question range: optional
    if (!isNil(uiQuizNumber)) {
      uiQuizParams['ui-quiz-start'] = uiQuizNumber;
      uiQuizParams['ui-quiz-end'] = uiQuizNumber;
    }

    return uiQuizParams;
  }

  camelToDashCase(input: string): string {
    return input.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  dashCaseToCamelCase(input: string): string {
    return input.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  }

  /**
   * Returns true if the specified widget path is a custom widget.
   */
  isCustomWidget(path: string) {
    return /widgets\/([^\/]+)\//.test(path);
  }

  saveUserImage(imageData: string, imageType: 'png' | 'jpeg' = 'jpeg'): Observable<string> {
    return this.http
      .post<string>(
        `/ws/user/image/v1-0`,
        { image_src: imageData, image_type: imageType },
        formHeaders,
      )
      .pipe(map((response: any) => response.url));
  }

  buildWidgetURL(widgetPath: string, widgetParams: SerializedParams): string {
    // Build params
    const encodedParams = Object.entries(widgetParams)
      .map(([key, value]) => {
        return `${key}=${value}`;
      })
      .join('&');

    // strip leading and trailing slashes (/)
    const normalizedWidgetPath = widgetPath.replace(/^\/|\/$/g, '');

    // Enable testing of embed locally
    if (environment.deploy === EnvironmentDeploy.Local) {
      return `${this.VIEWER_BASE_URL}/?${encodedParams}`;
    } else {
      return `${this.APP_BASE_URL}/${normalizedWidgetPath}/?${encodedParams}`;
    }
  }

  private buildThumbnailURL(thumbPath: string): string {
    return `${this.APP_BASE_URL}${thumbPath}`;
  }

  private buildDownloadURL(token: string): string {
    return `${this.APP_BASE_URL}/download?t=${token}`;
  }

  /**
   * Recursively set target widget field values from source data.
   */
  private deepExtendWidgetOptions(target: any, source: any) {
    Object.entries(target).forEach(([targetField, targetValue]) => {
      const targetType = typeof targetValue;

      // account for variation in source name
      const validFieldNames = [
        // uiCameraMode
        targetField,
        // ui-camera-model
        this.camelToDashCase(targetField),
        // cameraMode
        this.getConfigName(targetField),
      ];

      validFieldNames.forEach((validName) => {
        if (source.hasOwnProperty(validName) && source[validName] !== null) {
          const sourceValue = source[validName];
          const sourceType = typeof sourceValue;
          // special case: account for initial value
          if (targetValue === null) {
            target[targetField] = sourceValue;
          } else if (targetType === sourceType) {
            // validate types
            // recursive set
            if (targetType === 'object') {
              this.deepExtendWidgetOptions(targetValue, sourceValue);
            } else {
              // set primitives
              target[targetField] = sourceValue;
            }
          }
        }
      });
    });
  }

  /**
   * expands a flat obect { 'initial.button': true, 'initial.handHint': true } ->
   * {
   *  initial: {
   *    'button': true,
   *    handHint': true
   *  }
   * }
   */
  private expandWidgetOptions(source: any): any {
    const target = {};
    Object.entries(source).forEach(([key, value]) => {
      const components = key.split('.');
      if (components.length > 1) {
        const parentFieldName = components[0];
        const childFieldName = components.slice(1).join('.');
        // create initial entry, else replace non-object types
        if (target[parentFieldName] === null || typeof target[parentFieldName] !== 'object') {
          target[parentFieldName] = {};
        }
        target[parentFieldName][childFieldName] = value;
      } else {
        target[key] = source[key];
      }
    });
    return target;
  }
}
